import React from 'react';
import { ArrowRight, FileCheck2, Signature, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Progress } from '../ui/Progress';
import { GateStatusPill } from '../common/StatusPills';
import { AdvanceStageDialog } from './AdvanceStageDialog';
import { summariseGate, STAGE_META, nextStage } from '../../utils/stages';
import type { ApplicationRecord, StageId } from '../../types/domain';
interface GatePanelProps {
  record: ApplicationRecord;
  stage: StageId;
  readOnly?: boolean;
}
function verdict(record: ApplicationRecord, stage: StageId) {
  const items = record.gates[stage] ?? [];
  const summary = summariseGate(items);
  const outstanding = items.filter(item => item.status === 'outstanding');
  if (summary.total === 0) {
    return 'No gate definition is configured for this stage on this record.';
  }
  if (summary.cleared) {
    return `All ${summary.total} gate items are met. The record is recommended to advance to ${nextStage(record.track, stage) ? STAGE_META[nextStage(record.track, stage)!].label : 'closure'}.`;
  }
  if (outstanding.length > 0) {
    return `Gate readiness ${summary.percent}%. ${outstanding.length} item${outstanding.length === 1 ? '' : 's'} outstanding — ${outstanding[0].label.toLowerCase()} is the binding constraint, owned by ${outstanding[0].owner}.`;
  }
  return `Gate readiness ${summary.percent}%. All items are in progress with none outstanding; advancement is recommended once partial items are confirmed.`;
}
export function GatePanel({
  record,
  stage,
  readOnly = false
}: GatePanelProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const items = record.gates[stage] ?? [];
  const summary = summariseGate(items);
  const target = nextStage(record.track, record.stage);
  const isCurrentStage = stage === record.stage;
  return <section className="rounded-sm border border-border bg-card" data-id="element-498" data-mp="35jixl52oek09">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3" data-id="element-499" data-mp="3ajf4pd6o6rk1">
        <div data-id="element-500" data-mp="39548jlaa67so">
          <h2 className="text-[13px] font-semibold tracking-tight" data-id="element-501" data-mp="391gf0m4u5ldy">
            {STAGE_META[stage].label} gate
          </h2>
          <p className="mt-0.5 text-[12px] text-muted-foreground" data-id="element-502" data-mp="2z5dbpcy8zgpf">
            Required artefacts and sign-offs before advancement
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-503" data-mp="2v5cq98cqe58v">
          <div className="w-32" data-id="element-504" data-mp="3rtrb9x4zil33">
            <Progress value={summary.percent} data-id="element-505" data-mp="3rspr935zqo98" />
          </div>
          <span className="font-mono text-[12px] text-muted-foreground" data-id="element-506" data-mp="3mv7ndr6tyz83">{summary.percent}%</span>
        </div>
      </header>

      <div className="grid gap-4 p-4 lg:grid-cols-[1.6fr_1fr]" data-id="element-507" data-mp="3i367lvageqdb">
        <ul className="divide-y divide-border" data-id="element-508" data-mp="3e2w18hir1xxx">
          {items.length === 0 && <li className="py-3 text-[12px] text-muted-foreground" data-id="element-509" data-mp="3e0tatwdoq2so">
              No gate items are configured for this stage.
            </li>}
          {items.map(item => <li key={item.id} className="flex items-start justify-between gap-3 py-2.5 first:pt-0" data-id="element-510" data-mp="9mbgfd30quf2">
              <div className="flex min-w-0 items-start gap-2.5" data-id="element-511" data-mp="1sdiln45piyw1">
                {item.kind === 'signoff' ? <Signature className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" data-id="element-512" data-mp="21buab0n0jmlu" /> : <FileCheck2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" data-id="element-513" data-mp="268kqdnytii79" />}
                <div className="min-w-0" data-id="element-514" data-mp="2g70f5kadhfdl">
                  <p className="text-[13px] leading-snug text-foreground" data-id="element-515" data-mp="2kzl70g9pxieh">{item.label}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground" data-id="element-516" data-mp="2p1ybl1cqba9o">
                    {item.kind === 'signoff' ? 'Sign-off' : 'Artefact'} · {item.owner}
                    {item.note ? ` — ${item.note}` : ''}
                  </p>
                </div>
              </div>
              <GateStatusPill status={item.status} data-id="element-517" data-mp="2lcvwfarjb4oq" />
            </li>)}
        </ul>

        <div className="flex flex-col justify-between gap-3 rounded-sm bg-uae-green p-3.5 text-white" data-id="element-518" data-mp="2laj7gdnczu26">
          <div data-id="element-519" data-mp="2v2p6268499qc">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/60" data-id="element-520" data-mp="2v38dgt054fq4">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-521" data-mp="2q4ne0vspq02q" />
              AI stage readiness
            </p>
            <p className="mt-2 text-[12px] leading-relaxed text-white/85" data-id="element-522" data-mp="22ejkxid5hipz">
              {verdict(record, stage)}
            </p>
            <dl className="mt-3 grid grid-cols-3 gap-2 text-center" data-id="element-523" data-mp="1xhb0fb4j52lk">
              <div className="rounded-sm bg-white/5 py-2" data-id="element-524" data-mp="2cdsalz0zgb6j">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-525" data-mp="27cl2tzyubbzv">Met</dt>
                <dd className="font-mono text-sm text-white" data-id="element-526" data-mp="3nr48lta29zks">{summary.met}</dd>
              </div>
              <div className="rounded-sm bg-white/5 py-2" data-id="element-527" data-mp="3isjgyi8vko7y">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-528" data-mp="3is025aeiufy5">Partial</dt>
                <dd className="font-mono text-sm text-white" data-id="element-529" data-mp="3qahcfiu81ys6">{summary.partial}</dd>
              </div>
              <div className="rounded-sm bg-white/5 py-2" data-id="element-530" data-mp="3vc6rraoh8ym2">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-531" data-mp="3hbvzc37irbys">Open</dt>
                <dd className="font-mono text-sm text-white" data-id="element-532" data-mp="3lc63dabr3sb0">{summary.outstanding}</dd>
              </div>
            </dl>
          </div>

          {!readOnly && isCurrentStage && <div data-id="element-533" data-mp="hvpl0cdqrabu">
              <Button size="sm" variant="secondary" className="w-full bg-white text-uae-green hover:bg-white/90" onClick={() => setDialogOpen(true)} disabled={!target} data-id="element-534" data-mp="hw8q2siam48g">
                {summary.cleared ? 'Advance to next stage' : 'Gate blocked — override to advance'}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-535" data-mp="vtxay0cd1bdi" />
              </Button>
              <p className="mt-1.5 text-[10px] leading-relaxed text-white/50" data-id="element-536" data-mp="1aigw5ippjkp1">
                {target ? summary.cleared ? `Next stage: ${STAGE_META[target].label}` : 'Overrides are attributed and recorded on the audit timeline.' : 'Final stage of the lifecycle.'}
              </p>
            </div>}
          {!isCurrentStage && <p className="text-[10px] leading-relaxed text-white/50" data-id="element-537" data-mp="rk4mlvuvw8sx">
              Historic gate — advancement actions are available on the current stage only.
            </p>}
        </div>
      </div>

      <AdvanceStageDialog record={record} open={dialogOpen} onOpenChange={setDialogOpen} gateCleared={summary.cleared} outstandingCount={summary.outstanding + summary.partial} data-id="element-538" data-mp="iqhvij1mftq2" />
    </section>;
}